import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	FormHelperText,
	FormLabel,
	Grid,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	useGetUserSignatureQuery,
	useUpdateUserProfileMutation,
	useUpdateUserSignatureMutation,
} from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import SignatureEditor from '@/components/SignatureEditor';
import { UnlayerSignatureSettingsCard } from '@/components/views/SettingsView/UserSettings/UnlayerSignatureSettingsCard';
import { getPrimaryEmail } from '@/components/views/SettingsView/BrokerageUsersTable/helpers';
import { UserEmailSelect } from '@/components/selects/UserEmailSelect';
import { DelegatedEmailsInput } from '@/components/DelegatedEmailEntry';
import { ProfileAvatarEditor } from '@/components/ProfileAvatarEditor';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { RoleField } from './RoleField';
import { BrokerGroupsField } from './BrokerGroupsField';

const PROFILE_SAVE_KEYS = [
	'phoneNumber',
	'linkedIn',
	'disclosureUrl',
	'aboutUser',
	'fspNumber',
	'role',
	'displayEmail',
];

export const UserProfileDrawerForm = ({ canEdit, isSelf = false, isSaving = false }) => {
	const formik = useFormikContext();
	const { values, initialValues, getFieldProps, setFieldValue, isSubmitting } = formik;
	const dispatch = useDispatch();
	const [isImageUploading, setIsImageUploading] = useState(false);

	const [updateUserProfile] = useUpdateUserProfileMutation();
	const [updateUserSignature, { isLoading: isUpdatingSignature }] =
		useUpdateUserSignatureMutation();

	const { getFeatureFlags } = useFeatureFlagContext();
	const unlayerEmailFeature = Boolean(getFeatureFlags('unlayerEmails')?.active);

	const primaryEmail = getPrimaryEmail(values);

	const legacySignatureUrl = useMemo(() => {
		const signatures = values?.emailSignatures ?? [];
		const match =
			signatures.find((sig) => sig.name === primaryEmail) ||
			signatures.find((sig) => sig.name === values?.email) ||
			signatures[0];
		return match?.signature ?? values?.signatureHtmlUrl ?? '';
	}, [primaryEmail, values]);

	const { data: legacySignatureHtml = '', isFetching: isFetchingSignature } =
		useGetUserSignatureQuery(
			{ url: legacySignatureUrl, id: values?.userId },
			{ skip: !legacySignatureUrl || !values?.userId || unlayerEmailFeature },
		);

	useEffect(() => {
		if (unlayerEmailFeature || !legacySignatureHtml) return;
		setFieldValue('signatureHtml', legacySignatureHtml);
	}, [legacySignatureHtml, setFieldValue, unlayerEmailFeature]);

	const handleLegacySignatureSave = async () => {
		if (!canEdit || !values?.email || !primaryEmail) return;
		try {
			await updateUserSignature({
				email: values.email,
				html: values.signatureHtml,
				name: primaryEmail,
				currentName: primaryEmail,
			}).unwrap();
			dispatch(showToast({ message: 'Signature saved successfully.', success: true }));
		} catch (err) {
			console.error('Error saving signature', err);
			dispatch(showToast({ message: 'Unable to save signature.', error: true }));
		}
	};

	const removeDelegatedEmail = async (e) => {
		if (!canEdit || !values?.email) return;
		const newDelegatedEmails = (values.delegatedEmails ?? []).filter(
			(delegatedEmail) => delegatedEmail !== e,
		);
		try {
			await updateUserProfile({
				email: values.email,
				properties: {
					delegatedEmails: newDelegatedEmails,
				},
			}).unwrap();
		} catch (err) {
			console.error('Failed to remove delegated email', err);
			dispatch(
				showToast({
					message: 'Failed to update delegated emails. Please try again.',
					error: true,
				}),
			);
			setFieldValue(
				'delegatedEmails',
				Array.from(new Set([...(values.delegatedEmails ?? []), e])),
			);
		}
	};

	const addDelegatedEmail = async (e) => {
		if (!canEdit || !values?.email) return;
		const currentEmails = values.delegatedEmails ?? [];
		const newDelegatedEmails = currentEmails.includes(e)
			? currentEmails
			: [...currentEmails, e];

		try {
			await updateUserProfile({
				email: values.email,
				properties: {
					delegatedEmails: newDelegatedEmails,
				},
			}).unwrap();
		} catch (err) {
			console.error('Failed to add delegated email', err);
			dispatch(
				showToast({
					message: 'Failed to update delegated emails. Please try again.',
					error: true,
				}),
			);
			setFieldValue(
				'delegatedEmails',
				(values.delegatedEmails ?? []).filter((email) => email !== e),
			);
		}
	};

	const isBusy =
		isSaving || isSubmitting || isUpdatingSignature || isFetchingSignature || isImageUploading;

	const isProfileDirty =
		PROFILE_SAVE_KEYS.some((key) => {
			if (key === 'displayEmail' && !isSelf) return false;
			const current = values[key];
			const initial = initialValues[key];
			if (key === 'displayEmail') {
				const currentEmail = typeof current === 'string' ? current : (current?.name ?? '');
				const initialEmail = typeof initial === 'string' ? initial : (initial?.name ?? '');
				return currentEmail !== initialEmail;
			}
			return current !== initial;
		}) ||
		!isEqual(
			[...(values.brokerGroupIds ?? [])].sort(),
			[...(initialValues.brokerGroupIds ?? [])].sort(),
		) ||
		!isEqual(
			[...(values.delegatedEmails ?? [])].sort(),
			[...(initialValues.delegatedEmails ?? [])].sort(),
		);

	return (
		<Form
			style={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
				width: '100%',
				height: '100%',
				overflow: 'hidden',
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 2 }}>
				<Stack spacing={2}>
					<Stack alignItems='center' spacing={1} sx={{ textAlign: 'center' }}>
						<ProfileAvatarEditor
							userId={values.userId}
							email={values.email}
							imageUrl={values.imageUrl}
							disabled={!canEdit}
							size={100}
							onImageUrlChange={(downloadUrl) =>
								setFieldValue('imageUrl', downloadUrl)
							}
							onUploadingChange={setIsImageUploading}
						/>
						<Box>
							<Typography variant='h5' fontWeight={700}>
								{values.name ?? 'User profile'}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								{primaryEmail}
							</Typography>
							{!canEdit && (
								<FormHelperText sx={{ ml: 0 }}>
									{"Only principal users can edit other users' profiles."}
								</FormHelperText>
							)}
						</Box>
						{isBusy && <CircularProgress size={22} />}
					</Stack>
					<Card variant='outlined' sx={{ backgroundColor: 'transparent' }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>Name</FormLabel>
									<TextField fullWidth value={values.name ?? ''} disabled />
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<RoleField canEdit={canEdit} />
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>Primary Email</FormLabel>
									{isSelf ? (
										<UserEmailSelect
											disabled={isBusy}
											minimal={false}
											value={
												typeof values.displayEmail === 'string'
													? {
															name: values.displayEmail,
															signature: values.displayEmail,
														}
													: values.displayEmail
											}
											delegatedEmails={values.delegatedEmails}
											onChange={(selectedEmail) => {
												setFieldValue('displayEmail', selectedEmail);
											}}
										/>
									) : (
										<TextField
											fullWidth
											disabled
											value={
												typeof values.displayEmail === 'string'
													? values.displayEmail
													: (values.displayEmail?.name ??
														primaryEmail ??
														'')
											}
										/>
									)}
									<FormHelperText sx={{ marginLeft: 0 }}>
										This email will display in your Statement of Advice PDF
									</FormHelperText>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<BrokerGroupsField canEdit={canEdit} isSelf={isSelf} />
								</Grid>
								<Grid size={12}>
									<DelegatedEmailsInput
										formik={formik}
										onAddEmail={(email) => {
											addDelegatedEmail(email);
										}}
										onRemoveEmail={(email) => {
											removeDelegatedEmail(email);
										}}
										disabled={!canEdit || isBusy}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>Phone</FormLabel>
									<TextField
										fullWidth
										name='phoneNumber'
										disabled={!canEdit}
										{...getFieldProps('phoneNumber')}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>LinkedIn</FormLabel>
									<TextField
										fullWidth
										name='linkedIn'
										disabled={!canEdit}
										{...getFieldProps('linkedIn')}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>FSP Number</FormLabel>
									<TextField
										fullWidth
										name='fspNumber'
										disabled={!canEdit}
										{...getFieldProps('fspNumber')}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<FormLabel>Disclosure URL</FormLabel>
									<TextField
										fullWidth
										name='disclosureUrl'
										disabled={!canEdit}
										{...getFieldProps('disclosureUrl')}
									/>
								</Grid>
								<Grid size={12}>
									<FormLabel>About</FormLabel>
									<TextField
										fullWidth
										name='aboutUser'
										multiline
										rows={4}
										disabled={!canEdit}
										{...getFieldProps('aboutUser')}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>

					<Typography variant='h6'>Email signatures</Typography>
					{unlayerEmailFeature ? (
						<UnlayerSignatureSettingsCard
							disabled={!canEdit || isBusy}
							userId={values?.userId}
						/>
					) : (
						<Card variant='outlined' sx={{ backgroundColor: 'transparent' }}>
							<CardContent>
								<SignatureEditor
									url={legacySignatureUrl}
									value={values.signatureHtml}
									setValue={(html) => setFieldValue('signatureHtml', html)}
									disabled={!canEdit || isUpdatingSignature}
									handleSave={handleLegacySignatureSave}
								/>
							</CardContent>
						</Card>
					)}
				</Stack>
			</Box>

			<FolioDrawerFormGuard />
			{canEdit && (
				<FolioDrawerFooter>
					<Button type='submit' variant='contained' disabled={!isProfileDirty || isBusy}>
						Save
					</Button>
				</FolioDrawerFooter>
			)}
		</Form>
	);
};

export default UserProfileDrawerForm;
